import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MAPBOX_ACCESS_TOKEN } from '../utils/Constants';

const MILES_TO_METERS = 1609.34;

interface RadiusSearchValue {
  address: string;
  lat: number;
  lng: number;
  radiusMiles: number;
}

interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number]; // [lng, lat]
}

interface RadiusSearchValueEditorProps {
  value: string;
  handleOnChange: (value: string) => void;
}

function parseValue(value: string): RadiusSearchValue | null {
  if (!value) return null;
  try {
    const decoded = atob(value);
    const parsed = JSON.parse(decoded);
    if (parsed.shapes?.[0]?.type === 'circle') {
      const shape = parsed.shapes[0];
      return {
        address: parsed.address || '',
        lat: shape.center.lat,
        lng: shape.center.lng,
        radiusMiles: Math.round(shape.radius / MILES_TO_METERS)
      };
    }
  } catch {}
  return null;
}

function serializeValue(data: RadiusSearchValue): string {
  const payload = {
    address: data.address,
    shapes: [{
      type: 'circle',
      center: { lat: data.lat, lng: data.lng },
      radius: Math.round(data.radiusMiles * MILES_TO_METERS)
    }]
  };
  return btoa(JSON.stringify(payload));
}

export default function RadiusSearchValueEditor({ value, handleOnChange }: RadiusSearchValueEditorProps) {
  const [address, setAddress] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [suggestions, setSuggestions] = useState<MapboxFeature[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Parse initial value on mount
  useEffect(() => {
    const parsed = parseValue(value);
    if (parsed) {
      setAddress(parsed.address);
      setRadiusMiles(parsed.radiusMiles);
      setSelectedLocation({ lat: parsed.lat, lng: parsed.lng });
    }
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
        `access_token=${MAPBOX_ACCESS_TOKEN}&country=us&types=address,place,postcode&limit=5`;
      const response = await fetch(url);
      const data = await response.json();
      setSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Mapbox geocoding error:', err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    setSelectedLocation(null);

    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(newAddress);
    }, 300);
  };

  const handleSelectSuggestion = (feature: MapboxFeature) => {
    const [lng, lat] = feature.center;
    setAddress(feature.place_name);
    setSelectedLocation({ lat, lng });
    setSuggestions([]);
    setShowSuggestions(false);

    // Serialize and emit the value
    const serialized = serializeValue({
      address: feature.place_name,
      lat,
      lng,
      radiusMiles
    });
    handleOnChange(serialized);
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRadius = Math.max(1, Math.min(500, parseInt(e.target.value) || 25));
    setRadiusMiles(newRadius);

    // If we have a selected location, update the value
    if (selectedLocation) {
      const serialized = serializeValue({
        address,
        lat: selectedLocation.lat,
        lng: selectedLocation.lng,
        radiusMiles: newRadius
      });
      handleOnChange(serialized);
    }
  };

  return (
    <div ref={containerRef} style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <input
          type="text"
          className="rule-value"
          value={address}
          onChange={handleAddressChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder="Enter address..."
          style={{ width: '450px' }}
        />
        {isLoading && (
          <div style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: 'var(--amplify-colors-neutral-60)'
          }}>
            ...
          </div>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            width: '450px',
            maxHeight: '200px',
            overflowY: 'auto',
            backgroundColor: 'var(--amplify-colors-neutral-0)',
            border: '1px solid var(--edulead-colors-border)',
            borderRadius: 'var(--amplify-components-button-border-radius)',
            boxShadow: 'var(--shadow-2)',
            zIndex: 1000
          }}>
            {suggestions.map((feature, index) => (
              <div
                key={feature.id}
                onClick={() => handleSelectSuggestion(feature)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--amplify-colors-neutral-100)',
                  borderBottom: '1px solid var(--edulead-colors-border)',
                  backgroundColor: hoveredIndex === index ? 'var(--amplify-colors-neutral-10)' : 'transparent'
                }}
              >
                {feature.place_name}
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontSize: '13px', color: 'var(--amplify-colors-neutral-80)' }}>within</span>
        <input
          type="number"
          className="rule-value"
          value={radiusMiles}
          onChange={handleRadiusChange}
          min={1}
          max={500}
          style={{ width: '60px', textAlign: 'center' }}
        />
        <span style={{ fontSize: '13px', color: 'var(--amplify-colors-neutral-80)' }}>miles</span>
      </div>
    </div>
  );
}
